import React, { useEffect, useMemo, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import Dropdown from '../common/dropdown/Dropdown';
import Button from '../components/global/Button';
import Wrapper from '../components/global/Wrapper';
import SettingHeader from '../components/SettingPage/SettingHeader';
import SettingSteps from '../components/SettingPage/SettingSteps';
import {
  useGetBusinessInformationQuery,
  useUpdateBusinessInformationMutation,
} from '../features/businessInformation/businessInformationSlice';
import { useGetBusinessSectorQuery } from '../features/businessRegistration/businessRegistrationSlice';
import { validateEmail } from '../lib/validateEmail';
import { OptionType } from '../types';
import { decryptJson } from '../utils/decryptJson';
import { encryptData } from '../utils/encryptJson';
import { logUserOut } from '../utils/logUserOut';
import { useNavigate, useSearchParams } from 'react-router-dom';
import customToast, { ToastType } from '../components_/Toast';

const SettingBusinessInformation = () => {
  const [searchParams] = useSearchParams();
  const step = searchParams.get('step');
  const navigate = useNavigate();
  //   console.log(step);
  const token = localStorage.getItem('login-token');
  const [disable, setDisabled] = useState(false);
  const { data } = useGetBusinessSectorQuery({ token });
  const sectors = decryptJson(data?.data);
  const options: OptionType[] = [
    { label: '0 year', value: '0' },
    { label: '1 year', value: '1' },
    { label: '2 years', value: '2' },
    { label: '3 years', value: '3' },
    { label: '4 years', value: '4' },
    { label: '5 years', value: '5' },
    // { label: '6 years', value: '6 years' },
    // { label: '7 years', value: '7 years' },
  ];
  const [businessEmail, setBusinessEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [tinNumber, setTinNumber] = useState('');
  const [otherBusinessStructure, setOtherBusinessStructure] = useState('');
  const [businessRegistrationNumber, setBusinessRegistrationNumber] = useState('');
  const [changingemail, setChangingemail] = useState(false);
  const [description, setDescription] = useState<boolean>();
  const [businessDescription, setBusinessDescription] = useState('');
  const [natureOfBusiness, setNatureOfBusiness] = useState('');
  const [selectedStructureOfBusiness, setSelectedStructureOfBusiness] = useState<any>({
    value: '',
    label: '',
  });
  const [selectedOption, setSelectedOption] = useState({ value: '', label: '' });

  const [selectedBusinessSegment, setSelectedBusinessSegment] = useState({
    value: '',
    label: '',
  });

  const validateInputsResult = useMemo(() => {
    const isSelectedBusinessSegment = selectedBusinessSegment
      ? selectedBusinessSegment.value !== ''
      : false;
    const isBusinessDescription = businessDescription?.length >= 80;
    setDescription(isBusinessDescription);
    const isNatureOfBusiness = natureOfBusiness ? natureOfBusiness !== '' : false;
    const isSelectedStructureOfBusiness = selectedStructureOfBusiness
      ? selectedStructureOfBusiness.value !== ''
      : false;
    const isSelectedOption = selectedOption ? selectedOption.value !== '' : false;
    const isBusinessEmail = validateEmail(businessEmail);
    return (
      isSelectedBusinessSegment &&
      isBusinessDescription &&
      isSelectedStructureOfBusiness &&
      isSelectedOption &&
      isBusinessEmail &&
      isNatureOfBusiness
    );
  }, [
    selectedBusinessSegment,
    businessDescription,
    selectedStructureOfBusiness,
    selectedOption,
    businessEmail,
    natureOfBusiness,
  ]);
  useEffect(() => {
    if (validateInputsResult) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [validateInputsResult]);

  const { data: businessInformation, isLoading, error } = useGetBusinessInformationQuery({ token });
  const businessDetails = businessInformation?.data;
  const [newDetials, setNewDetails] = useState<any>({});

  useEffect(() => {
    if (businessDetails) {
      setNewDetails(decryptJson(businessDetails));
    }
  }, [businessDetails]);

  useEffect(() => {
    if (error) {
      customToast('User does not have a business information.', ToastType.error);
    }
  }, [error]);

  useEffect(() => {
    if (newDetials?.businessName) {
      setBusinessName(newDetials.businessName);
    }
    if (newDetials?.businessEmail) {
      setBusinessEmail(newDetials.businessEmail);
    }
    if (newDetials?.businessDescription) {
      setBusinessDescription(newDetials.businessDescription);
    }
    if (newDetials?.businessNature) {
      setNatureOfBusiness(newDetials.businessNature);
    }
    if (newDetials?.businessSegment) {
      setSelectedBusinessSegment({
        value: newDetials.businessSegment,
        label: newDetials.businessSegment,
      });
    }
    if (newDetials?.yearsInBusiness) {
      setSelectedOption({
        value: newDetials.yearsInBusiness,
        label: newDetials.yearsInBusiness,
      });
    }
    if (newDetials?.tin) {
      setTinNumber(newDetials.tin);
    }
    if (newDetials?.businessStructure) {
      setSelectedStructureOfBusiness({
        value: newDetials.businessStructure,
        label: newDetials.businessStructure,
      });
    }
    if (newDetials?.rcNumber) {
      setBusinessRegistrationNumber(newDetials.rcNumber);
    }
  }, [
    newDetials.businessName,
    newDetials.businessEmail,
    newDetials.businessDescription,
    newDetials.businessNature,
    newDetials.businessSegment,
    newDetials.yearsInBusiness,
    newDetials.tin,
    newDetials.businessStructure,
    newDetials.rcNumber,
  ]);

  const [updateBusinessInformation, { isLoading: isUpdating }] =
    useUpdateBusinessInformationMutation();
  const handleBusinessInformationUpdate = async () => {
    const dataUpdated = {
      businessName: businessName,
      businessNature: natureOfBusiness,
      businessEmail: businessEmail,
      businessDescription: businessDescription,
      rcNumber: businessRegistrationNumber,
      businessStructure:
        selectedStructureOfBusiness.value === 'Other'
          ? otherBusinessStructure
          : selectedStructureOfBusiness.value,
      businessSegment: selectedBusinessSegment.value,
      tin: tinNumber,
      yearsInBusiness: Number(selectedOption.value),
    };
    try {
      const body = encryptData(dataUpdated);
      const res = await updateBusinessInformation({ token, body });

      customToast('Business information successfully saved.', ToastType.success);
      setBusinessEmail('');
      setBusinessRegistrationNumber('');
      setBusinessDescription('');
      setNatureOfBusiness('');
      setSelectedOption({ value: '', label: '' });
      setSelectedStructureOfBusiness({ value: '', label: '' });
      setSelectedBusinessSegment({ value: '', label: '' });
      setTinNumber('');
      setBusinessName('');
      navigate('/dashboard');

      searchParams.set('step', '4');
    } catch (error: any) {
      if (error?.status === 593) {
        logUserOut(error?.status);
      }

      customToast(error.message, ToastType.error);
    }
  };

  return (
    <Wrapper className=' space-y-3 md:space-y-8  !mr-0'>
      <div className=' md:flex  xl:flex '>
        <div className='min-w-[200px] w-[200px] hidden  md:hidden lg:flex xl:flex'>
          <SettingSteps step={step ? step : 3} />
        </div>
        <div className='flex-2 md:px-[35px] pt-[12px] md:pt-12  md:w-[100%] lg:w-[70%] xl:w-[70%] m-auto'>
          <SettingHeader title='Business Information' />
          <div className=' mb-[68px]'>
            <div className='text-[#F51010]  flex items-center text-[15px] font-normal mt-[15px]'>
              *<div className='ml-[7px]'>Compulsory fields</div>
            </div>
            <ClipLoader
              className='mt-[20px] mx-auto'
              color={'#F8B400'}
              loading={isLoading || isUpdating}
              size={50}
            />
          </div>

          <form>
            <div className='mb-[27px] flex items-center w-full'>
              <div className='w-full   md:mr-[18px] flex-1'>
                <label className='flex items-center  relative text-[13px] mb-[5px] text-[#212121] '>
                  Name of Business/Company
                  <span className=' text-[#F51010] pl-[4px] pt-1 block md:hidden '>*</span>
                </label>

                <input
                  value={newDetials?.businessName}
                  className='w-full sm:w-full md:width-full relative h-[45px] px-4 border-[1px] border-[#B8C9C9] rounded-[2px] focus:border-[#F8B400] outline-none '
                  onChange={(e) => {
                    setNewDetails({
                      ...newDetials,
                      businessName: e.target.value,
                    });
                  }}
                />
              </div>
              <div className=' pt-3 hidden md:block  relative h-[45px] text-[#F51010] mt-[15px] '>
                *
              </div>
            </div>
            <div className='mb-[27px] flex items-center w-full'>
              <div className='w-full   md:mr-[18px] flex-1'>
                <label className='flex items-center  relative text-[13px] mb-[5px] text-[#212121] '>
                  Nature of business
                  <span className=' text-[#F51010] pl-[4px] pt-1 block md:hidden '>*</span>
                </label>

                <input
                  value={newDetials?.businessNature}
                  className='w-full sm:w-full relative h-[45px] px-4 border-[1px] border-[#B8C9C9] rounded-[2px] focus:border-[#F8B400] outline-none '
                  onChange={(e) => {
                    setNewDetails({
                      ...newDetials,
                      businessNature: e.target.value,
                    });
                  }}
                />
              </div>
              <div className=' pt-3 hidden md:block  relative h-[45px] text-[#F51010] mt-[15px] '>
                *
              </div>
            </div>
            <div className='flex items-center'>
              <div className='w-full md:pr-[10px]  flex flex-col  rounded-[5px] outline-none  focus:border-[#f8b400]'>
                <label htmlFor='describeBusiness' className='pb-2'>
                  Describe your business
                  {newDetials?.businessDescription?.length < 80 && (
                    <span className=' ml-9 text-errorRed font-Poppins  text-[10px]'>
                      (must be 80 words minimum)
                    </span>
                  )}
                </label>
                <textarea
                  className='w-full  sm:w-full relative px-4 border-[1px] py-3 resize-none border-[#B8C9C9] rounded-[2px] focus:border-[#F8B400] outline-none '
                  placeholder='Please describe your business in nothing less than 80 words'
                  value={newDetials?.businessDescription}
                  onChange={(e) => {
                    setNewDetails({
                      ...newDetials,
                      businessDescription: e.target.value,
                    });
                  }}
                  minLength={80}
                />
              </div>
              <div className='md:ml-[10px] pt-3 hidden md:block  relative h-[45px] text-[#F51010] mt-[15px] '>
                *
              </div>
            </div>

            <div className='mb-[27px] mt-[27px] flex items-center w-full'>
              <div className='w-full  md:mr-[18px]'>
                <label className='flex items-center  relative text-[13px] mb-[5px] text-[#212121] '>
                  <div className='flex flex-row'>
                    Business email address
                    <span className=' text-[#F51010] pl-[4px] pt-1 block md:hidden '>*</span>
                  </div>
                  {changingemail && !validateEmail(businessEmail) && (
                    <span className='text-xs text-[red] pl-4'>Invalid email</span>
                  )}
                </label>

                <input
                  className='w-full sm:w-full relative h-[45px] px-4 border-[1px] border-[#B8C9C9] rounded-[2px] focus:border-[#F8B400] outline-none '
                  onChange={(e) => {
                    setChangingemail(true);
                    setNewDetails({
                      ...newDetials,
                      businessEmail: e.target.value?.toLowerCase(),
                    });
                  }}
                  value={newDetials?.businessEmail}
                />
              </div>
              <div className=' pt-3 hidden md:block  relative h-[45px] text-[#F51010] mt-[15px] '>
                *
              </div>
            </div>
            <div className='flex items-center w-full'>
              <div className='w-full text-xs mb-[27px] '>
                <Dropdown
                  className='border rounded-[2px] h-[52px] text-[#212121] '
                  // SelectedValueStyles='text-[14px] font-poppins font-medium !text-black'
                  label={'Business Structure:'}
                  labelStyles='text-[#212121] text-sm font-poppins font-normal leading-[18px] pb-[5px] '
                  options={[
                    {
                      value: 'Unregistered Business',
                      label: 'Unregistered Business',
                    },
                    {
                      value: 'Business Name',
                      label: 'Business Name',
                    },
                    {
                      value: 'Limited Liability Corporation ',
                      label: 'Limited Liability Corporation ',
                    },
                    {
                      value: 'Incorporated Trustees(Religious body, NGO, Association)',
                      label: 'Incorporated Trustees(Religious body, NGO, Association)',
                    },
                    {
                      value: 'Other',
                      label: 'Other',
                    },
                  ]}
                  placeholder={selectedStructureOfBusiness.label}
                  setSelectedOption={(e: any) =>
                    setNewDetails({
                      ...newDetials,
                      businessStructure: e.value,
                    })
                  }
                  width='100%'
                  value={newDetials?.businessStructure}
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
                />
              </div>
              <div className='lg:ml-[18px] md:ml-[20px]  justify-end hidden flex-1 md:flex xl:flex   text-[#F51010] mt-[15px] '>
                *
              </div>
            </div>
            {selectedStructureOfBusiness.value === 'Other' && (
              <div className='mb-[27px] flex items-center w-full'>
                <div className='w-full mr-[10px]  md:mr-[18px]'>
                  <label className='flex items-center  relative text-[13px] mb-[5px] text-[#212121] '>
                    Type other business structure here
                  </label>

                  <input
                    value={otherBusinessStructure}
                    className='w-full sm:w-80 md:w-[459px] lg:w-[483px] relative h-[45px] px-4 border-[1px] border-[#B8C9C9] rounded-[2px] focus:border-[#F8B400] outline-none '
                    onChange={(e) => setOtherBusinessStructure(e.target.value)}
                  />
                </div>
              </div>
            )}
            <div className=' flex items-center w-full'>
              <div className='w-full text-xs mb-[27px] '>
                <Dropdown
                  className='border rounded-[2px] h-[52px] text-[#212121] '
                  // SelectedValueStyles='text-[14px] font-poppins font-medium !text-black'
                  label='Business Segments:'
                  labelStyles='text-[#212121] text-sm font-poppins font-normal leading-[18px] pb-[5px] '
                  options={
                    data &&
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    sectors.map((item: any) => {
                      return {
                        label: item.sector,
                        value: item.sector,
                      };
                    })
                  }
                  placeholder={selectedBusinessSegment.label}
                  setSelectedOption={(e: any) =>
                    setNewDetails({
                      ...newDetials,
                      businessSegment: e.value,
                    })
                  }
                  width='100%'
                  value={newDetials?.businessSegment}

                  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
                />
              </div>
              <div className='lg:ml-[18px] md:ml-[20px] justify-end hidden flex-1 md:flex xl:flex  text-[#F51010] mt-[15px] '>
                *
              </div>
            </div>
            <div className=' flex items-center w-full'>
              <div className='w-[100%] text-xs mb-[27px]  '>
                <Dropdown
                  className='border rounded-[2px] h-[52px] text-[#212121] '
                  // SelectedValueStyles='text-[14px] font-poppins font-medium !text-black'
                  label='Years in business:'
                  labelStyles='text-[#212121] text-sm font-poppins font-normal leading-[18px] pb-[5px] '
                  options={options}
                  placeholder={selectedOption.label}
                  setSelectedOption={(e: any) =>
                    setNewDetails({
                      ...newDetials,
                      yearsInBusiness: e.value,
                    })
                  }
                  width='100%'
                  value={newDetials?.yearsInBusiness}
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
                />
              </div>
              <div className='lg:ml-[18px] md:ml-[20px] justify-end hidden opacity-0 flex-1 md:flex xl:flex  text-[#F51010] mt-[15px] '>
                *
              </div>
            </div>
            <div className='mb-[27px] flex items-center w-full'>
              <div className='w-full  md:mr-[18px]'>
                <label className='flex items-center  relative text-[13px] mb-[5px] text-[#212121] '>
                  TIN Number
                </label>

                <input
                  value={newDetials?.tin}
                  className='w-full relative h-[45px] px-4 border-[1px] border-[#B8C9C9] rounded-[2px] focus:border-[#F8B400] outline-none '
                  onChange={(e) =>
                    setNewDetails({
                      ...newDetials,
                      tin: e.target.value,
                    })
                  }
                />
              </div>
              <div className=' pt-3 hidden md:block opacity-0 relative h-[45px] text-[#F51010] mt-[15px] '>
                *
              </div>
            </div>
            <div className='mb-[27px] flex items-center w-full'>
              <div className='w-full md:mr-[18px]'>
                <label className='flex items-center  relative text-[13px] mb-[5px] text-[#212121] '>
                  Business Registration Number
                </label>

                <input
                  value={newDetials?.rcNumber}
                  className='w-full  relative h-[45px] px-4 border-[1px] border-[#B8C9C9] rounded-[2px] focus:border-[#F8B400] outline-none '
                  onChange={(e) =>
                    setNewDetails({
                      ...newDetials,
                      rcNumber: e.target.value,
                    })
                  }
                />
              </div>
              <div className=' pt-3 hidden md:block opacity-0  relative h-[45px] text-[#F51010] mt-[15px] '>
                *
              </div>
            </div>
            <Button
              type='button'
              disabled={disable}
              onClick={handleBusinessInformationUpdate}
              size='md'
              loading={isUpdating}
              className='text-[13px] font-Poppins font-medium text-white mt-9 bg-green rounded-sm !py-[9px] !px-[49px] '
            >
              SAVE CHANGES
            </Button>
          </form>
        </div>
      </div>
    </Wrapper>
  );
};

export default SettingBusinessInformation;
